// useMyListings.js
// Custom hook for managing user's listings state and operations

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../services/firebase';
import { deleteDoc, doc, updateDoc } from 'firebase/firestore';
import fetchListings from '../../utils/fetchListings';

const useMyListings = () => {
  const [allUserListings, setAllUserListings] = useState([]); // All user's listings (unfiltered)
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: "",
    minPrice: "",
    maxPrice: ""
  });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const navigate = useNavigate();

  // Apply filters and search client-side
  const filteredListings = useMemo(() => {
    let result = [...allUserListings];

    // Apply category filter
    if (filters.category) {
      result = result.filter(listing => listing.category === filters.category);
    }

    // Apply price filters
    if (filters.minPrice && filters.minPrice !== "") {
      const minPrice = Number(filters.minPrice);
      result = result.filter(listing => listing.price >= minPrice);
    }
    if (filters.maxPrice && filters.maxPrice !== "") {
      const maxPrice = Number(filters.maxPrice);
      result = result.filter(listing => listing.price <= maxPrice);
    }

    // Apply search filter
    if (search) {
      result = result.filter(listing =>
        listing.title && listing.title.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sort) {
        case "priceLow":
          return a.price - b.price;
        case "priceHigh":
          return b.price - a.price;
        case "newest":
        default:
          return b.createdAt?.seconds - a.createdAt?.seconds;
      }
    });

    return result;
  }, [allUserListings, filters, search, sort]);

  // Fetch all listings created by the current user (without filters)
  useEffect(() => {
    const loadMyListings = async () => {
      const user = auth.currentUser;
      if (!user) {
        alert("You must be logged in to view your listings.");
        return navigate("/login");
      }

      try {
        setLoading(true);
        // Fetch ALL listings created by the current user (no filters applied here)
        const listings = await fetchListings({
          userId: user.uid,
          excludeArchived: true, // Don't show archived listings
          orderBy: "createdAt",
          orderDir: "desc"
        });
        setAllUserListings(listings);
      } catch (error) {
        setAllUserListings([]);
      } finally {
        setLoading(false);
      }
    };

    loadMyListings();
  }, [navigate]); // Only re-fetch when navigate changes (mount/unmount)

  // Delete a listing by ID (now uses archive for better data preservation)
  const handleDelete = async (id) => {
    const listing = allUserListings.find(l => l.id === id);
    const isSold = listing?.status === 'sold';
    
    if (isSold) {
      // For sold items, suggest archiving instead of deleting
      if (window.confirm("This item has been sold. Would you like to archive it instead of deleting? Archiving preserves your sale history.")) {
        return handleArchive(id);
      }
    }
    
    if (window.confirm("Are you sure you want to permanently delete this listing? This action cannot be undone and you'll lose all associated data.")) {
      try {
        await deleteDoc(doc(db, "listings", id));
        setAllUserListings((prev) => prev.filter((listing) => listing.id !== id));
        alert("Listing deleted permanently.");
      } catch (error) {
        alert("Unable to delete listing. Please try again or contact support if the issue persists.");
      }
    }
  };

  // Navigate to edit listing page
  const handleEdit = (listingId) => {
    navigate(`/edit-listing/${listingId}`);
  };

  // Mark listing as sold
  const handleMarkSold = async (id) => {
    if (window.confirm("Mark this item as sold? This will keep it visible in your sales history.")) {
      try {
        await updateDoc(doc(db, "listings", id), {
          status: "sold",
          soldAt: new Date()
        });
        // Update the listing status in state
        setAllUserListings(prev => prev.map(listing => 
          listing.id === id 
            ? { ...listing, status: "sold", soldAt: new Date() }
            : listing
        ));
        alert("Item marked as sold successfully!");
      } catch (error) {
        alert("Unable to mark listing as sold. Please try again.");
      }
    }
  };

  // View sale details for sold items
  const handleViewDetails = (listingId) => {
    navigate(`/sale-details/${listingId}`);
  };

  // Contact buyer for sold items
  const handleContactBuyer = async (listingId) => {
    const listing = allUserListings.find(l => l.id === listingId);
    
    if (!listing || listing.status !== 'sold') {
      alert("This item hasn't been sold yet.");
      return;
    }

    // Check if we have buyer ID stored in the listing
    const buyerId = listing.soldTo;
    
    if (!buyerId) {
      alert("Buyer contact information not available for this sale.");
      return;
    }

    if (buyerId === auth.currentUser.uid) {
      alert("You cannot message yourself.");
      return;
    }

    try {
      // Import messaging utilities
      const { startDirect } = await import('../../utils/messageUtils');
      
      // Create or get existing chat with the buyer
      const chatId = await startDirect(buyerId, auth.currentUser.uid, listingId);
      
      navigate(`/direct/${chatId}`);
    } catch (error) {
      alert(`Unable to start conversation: ${error.message}`);
    }
  };

  // Archive listing (soft delete)
  const handleArchive = async (id) => {
    const listing = allUserListings.find(l => l.id === id);
    const isSold = listing?.status === 'sold';
    const confirmMessage = isSold 
      ? "Archive this completed sale? It will be moved to your archived sales history."
      : "Archive this listing? It will be removed from the marketplace but kept in your records.";
    
    if (window.confirm(confirmMessage)) {
      try {
        await updateDoc(doc(db, "listings", id), {
          status: "archived",
          archivedAt: new Date()
        });
        // Remove from current listings view
        setAllUserListings((prev) => prev.filter((listing) => listing.id !== id));
        alert(isSold ? "Sale archived successfully." : "Listing archived successfully.");
      } catch (error) {
        alert("Unable to archive listing. Please try again.");
      }
    }
  };

  // View archived items (optional feature)
  const handleViewArchived = () => {
    navigate('/my-listings/archived');
  };

  return {
    listings: filteredListings,
    totalListings: allUserListings.length, // Total listings before filtering
    loading,
    filters,
    setFilters,
    search,
    setSearch,
    sort,
    setSort,
    handleDelete,
    handleEdit,
    handleMarkSold,
    handleViewDetails,
    handleContactBuyer,
    handleArchive,
    handleViewArchived
  };
};

export default useMyListings;